import { sfxUiTick } from '@/audio/sfx';
import { markBusy } from '@/core/idleGate';
import { hapticTick } from '@/ui/haptics';
import { registerBackAction } from '@/ui/backNav';

/**
 * dom.ts — tiny DOM builders shared by the HUD + menu screens.
 * M86: every shared button carries the click PAIR — sfxUiTick + a haptic
 * tick — so menu taps sound AND feel the same everywhere.
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  parent: HTMLElement,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  parent.appendChild(node);
  return node;
}

/** A button with a label + click handler (the workhorse of every menu). */
export function btn(
  label: string,
  className: string,
  parent: HTMLElement,
  onClick: () => void,
): HTMLButtonElement {
  const b = el('button', className, parent);
  b.type = 'button';
  b.textContent = label;
  b.addEventListener('click', () => {
    sfxUiTick();
    hapticTick();
    markBusy(500); // loading round 2: a navigation is starting — background work waits
    onClick();
  });
  return b;
}

/** The one back glyph — a chevron pointing the way out (M114b). */
const BACK_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg>';

export interface BackButtonOptions {
  /** Accessible name — 'Back' by default; 'Close' where that reads truer. */
  label?: string;
  /** Pin it to the SCREEN's upper-left corner (default). Pass false when the
   *  screen already lays out a top-left header row for it to sit in
   *  (garage / shop / race lobby / quick race / challenge friends). */
  corner?: boolean;
  /** M114b: opt this screen OUT of the swipe-back gesture. Used where "back"
   *  means leaving a LIVE multiplayer room — an accidental edge swipe must
   *  never disconnect you — or where the screen owns horizontal swipes. */
  swipe?: boolean;
  /** Extra classes, for the rare per-screen nudge. */
  className?: string;
}

/**
 * THE back button (M114b, owner rule) — icon-only, upper-left, identical on
 * every menu screen. Neutral glass: never red (that's destructive-only, the
 * M92 rule) and never gold-filled (that's affirmative). Registers itself with
 * backNav so the left-edge swipe gesture picks it up automatically; a screen
 * with no back button therefore has no swipe by construction.
 */
export function backButton(
  parent: HTMLElement,
  onBack: () => void,
  opts: BackButtonOptions = {},
): HTMLButtonElement {
  const corner = opts.corner !== false;
  const cls =
    'btn btn-back' + (corner ? ' btn-back-corner' : '') + (opts.className ? ` ${opts.className}` : '');
  const b = el('button', cls, parent);
  b.type = 'button';
  b.innerHTML = BACK_SVG;
  b.setAttribute('aria-label', opts.label ?? 'Back');
  b.addEventListener('click', () => {
    sfxUiTick();
    hapticTick();
    markBusy(500);
    onBack();
  });
  if (opts.swipe !== false) registerBackAction(b, onBack);
  return b;
}

/** Icon set for the big menu cards (title screen + online hub). All inherit
 *  currentColor so each card tints its icon via the --mc-accent variable. */
export const MENU_ICONS = {
  globe:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9"/><ellipse cx="12" cy="12" rx="4.2" ry="9"/>' +
    '<path d="M3.6 8.6h16.8M3.6 15.4h16.8"/></svg>',
  bolt:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M13.4 2 5 13.4h5l-1.6 8.6L17 10.4h-5.2L13.4 2Z"/></svg>',
  people:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<circle cx="8.2" cy="8.6" r="3.1"/><path d="M2.4 19.2c0-3.1 2.6-5.2 5.8-5.2s5.8 2.1 5.8 5.2v.8H2.4v-.8Z"/>' +
    '<circle cx="16.6" cy="7.4" r="2.6" opacity="0.72"/>' +
    '<path d="M15.4 13.3c3-.2 6.2 1.7 6.2 4.9v.9h-5.4v-.6c0-2-.6-3.8-1.9-5.1l1.1-.1Z" opacity="0.72"/></svg>',
  car:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M4.2 12.5 5.6 8a2 2 0 0 1 1.9-1.4h9a2 2 0 0 1 1.9 1.4l1.4 4.5c.7.3 1.2 1 1.2 1.9v3.7a1 1 0 0 1-1 1h-1.2a1 1 0 0 1-1-1v-.9H5.2v.9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-3.7c0-.9.5-1.6 1.2-1.9Zm2.7-4.1-1 3.3h12.2l-1-3.3a.6.6 0 0 0-.6-.4h-9a.6.6 0 0 0-.6.4ZM6 16.2a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Zm12 0a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Z"/></svg>',
} as const;

const CHEVRON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>';

export interface MenuCardOptions {
  icon: string; // one of MENU_ICONS (raw SVG markup)
  title: string;
  /** The accent-coloured caps kicker line under the title (reference art). */
  sub: string;
  /** Optional third line: a quiet italic description (reference art). */
  desc?: string;
  /** Extra class, e.g. 'mcard-primary' for the gold hero card. */
  className?: string;
  /** Accent tint for icon + kicker + chevron (mcard-primary forces gold). */
  accent?: string;
}

/** A big tappable menu card (reference art): angled icon zone · italic title +
 *  accent caps kicker + italic description · chevron. */
export function menuCard(
  opts: MenuCardOptions,
  parent: HTMLElement,
  onClick: () => void,
): HTMLButtonElement {
  const b = el('button', `mcard${opts.className ? ` ${opts.className}` : ''}`, parent);
  b.type = 'button';
  if (opts.accent) b.style.setProperty('--mc-accent', opts.accent);
  const icon = el('span', 'mcard-icon', b);
  icon.innerHTML = opts.icon;
  const text = el('span', 'mcard-text', b);
  el('span', 'mcard-title', text).textContent = opts.title;
  el('span', 'mcard-sub', text).textContent = opts.sub;
  if (opts.desc) el('span', 'mcard-desc', text).textContent = opts.desc;
  const chev = el('span', 'mcard-chevron', b);
  chev.innerHTML = CHEVRON_SVG;
  b.addEventListener('click', () => {
    sfxUiTick();
    hapticTick();
    markBusy(500); // loading round 2: a navigation is starting — background work waits
    onClick();
  });
  return b;
}

/** The championship crest for the tour finale: gold laurels around a globe in
 *  a hex plate — the "world title" mark over the WORLD CHAMPION headline. */
export function championBadgeSvg(): string {
  const leaf = (x: number, y: number, r: number, flip: number): string =>
    `<ellipse cx="${x}" cy="${y}" rx="7.2" ry="2.9" transform="rotate(${r * flip} ${x} ${y})" />`;
  const branch = (flip: number): string =>
    `<g fill="#caa64b" opacity="0.9" transform="${flip < 0 ? 'translate(128 0) scale(-1 1)' : ''}">` +
    `<path d="M22 64 C14 48 16 30 30 18" fill="none" stroke="#caa64b" stroke-width="2.4"/>` +
    leaf(21, 55, -38, 1) +
    leaf(17, 44, -12, 1) +
    leaf(18, 33, 14, 1) +
    leaf(24, 23, 38, 1) +
    leaf(32, 15, 58, 1) +
    `</g>`;
  return (
    '<svg class="champ-badge" viewBox="0 0 128 84" aria-hidden="true">' +
    '<defs><linearGradient id="champ-hex" x1="0" y1="0" x2="0" y2="1">' +
    '<stop offset="0" stop-color="#3a3016"/><stop offset="1" stop-color="#191307"/></linearGradient>' +
    '<linearGradient id="champ-gold" x1="0" y1="0" x2="0" y2="1">' +
    '<stop offset="0" stop-color="#ffe9a3"/><stop offset="0.55" stop-color="#f4c542"/>' +
    '<stop offset="1" stop-color="#c8891a"/></linearGradient></defs>' +
    branch(1) +
    branch(-1) +
    '<path d="M64 8 88 21.5v27L64 62 40 48.5v-27Z" fill="url(#champ-hex)" ' +
    'stroke="url(#champ-gold)" stroke-width="2.6"/>' +
    '<g fill="none" stroke="url(#champ-gold)" stroke-width="1.8">' +
    '<circle cx="64" cy="35" r="11.5"/><ellipse cx="64" cy="35" rx="5" ry="11.5"/>' +
    '<path d="M53.4 30.6h21.2M53.4 39.4h21.2"/></g>' +
    '<circle cx="64" cy="72" r="3" fill="url(#champ-gold)"/>' +
    '<path d="M48 72h10M70 72h10" stroke="url(#champ-gold)" stroke-width="2" stroke-linecap="round"/>' +
    '</svg>'
  );
}

/** Inline-SVG trophy badge (gold / silver / bronze), tinted via CSS classes. */
export function trophySvg(kind: 'gold' | 'silver' | 'bronze'): string {
  return (
    `<svg class="trophy trophy-${kind}" viewBox="0 0 24 24" aria-hidden="true">` +
    '<path d="M7 3h10v2h3v3c0 2.4-1.8 4.3-4.1 4.4A5 5 0 0 1 13 15v2h3v3H8v-3h3v-2a5 5 0 0 1-2.9-2.6C5.8 12.3 4 10.4 4 8V5h3V3Zm-1 4v1c0 1.1.7 2.1 1.7 2.5A8 8 0 0 1 7 7H6Zm12 0h-1a8 8 0 0 1-.7 3.5c1-.4 1.7-1.4 1.7-2.5V7Z"/>' +
    '</svg>'
  );
}

const CUP_STOPS: Record<'gold' | 'silver' | 'bronze', [string, string, string]> = {
  gold: ['#ffe9a3', '#f4c542', '#c8891a'],
  silver: ['#f4f7fb', '#c9d4e0', '#8d9aa9'],
  bronze: ['#f0c294', '#cd8f4e', '#8f5620'],
};

/** The BIG podium cup (M52) — the top-3 finish moment. Rendered inside
 *  `.results-cupwrap`, which adds the ray burst + shine sweep animations. */
export function bigCupSvg(kind: 'gold' | 'silver' | 'bronze'): string {
  const [hi, mid, lo] = CUP_STOPS[kind];
  const g = `cupg-${kind}`;
  return (
    `<svg class="bigcup" viewBox="0 0 120 130" aria-hidden="true">` +
    `<defs><linearGradient id="${g}" x1="0" y1="0" x2="0.9" y2="1">` +
    `<stop offset="0" stop-color="${hi}"/><stop offset="0.55" stop-color="${mid}"/>` +
    `<stop offset="1" stop-color="${lo}"/></linearGradient></defs>` +
    // Handles (behind the bowl).
    `<path d="M27 27 C7 27 7 55 31 59" fill="none" stroke="url(#${g})" stroke-width="7" stroke-linecap="round"/>` +
    `<path d="M93 27 C113 27 113 55 89 59" fill="none" stroke="url(#${g})" stroke-width="7" stroke-linecap="round"/>` +
    // Bowl.
    `<path d="M28 18 h64 v13 c0 23 -13 36 -32 36 s-32 -13 -32 -36 z" fill="url(#${g})"/>` +
    // Rim.
    `<rect x="25" y="14" width="70" height="8" rx="4" fill="${hi}"/>` +
    // A soft highlight inside the bowl for volume.
    `<path d="M36 24 c0 20 8 32 24 35 c-14 0 -26 -11 -26 -33 z" fill="rgba(255,255,255,0.28)"/>` +
    // Stem + base + plinth.
    `<path d="M53 67 h14 l5 20 h-24 z" fill="url(#${g})"/>` +
    `<rect x="38" y="87" width="44" height="9" rx="3" fill="url(#${g})"/>` +
    `<rect x="30" y="98" width="60" height="14" rx="4" fill="#232837"/>` +
    `<rect x="30" y="98" width="60" height="4" rx="2" fill="#2f364a"/>` +
    `</svg>`
  );
}
